import { PricingPlanCard, PricingPlanContainer } from "ui/data-display/PricingPlan/PricingPlanCard";
import {
    useGetPricingPlansQuery,
    useSelectPricingPlanMutation,
    useChangePricingPlanMutation,
    useCancelSubscriptionMutation,
    useGetUpcomingSubscriptionQuery, } from "services/payments";
import { selectActiveSubscription, selectUser } from "features/users/usersSlice";
import { useAppSelector } from "app/hooks";
import { useTranslation } from "react-i18next";
import { PricingPlan, PricingPlanSubscription } from "types";
import { Container } from "ui/layout/Containers";
import { Heading } from "ui/typography/Heading";
import { Button } from "ui/atoms/buttons";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";


export const SelectPricingPlanPage = () => {

    const { data: pricingPlans, isLoading, isError } = useGetPricingPlansQuery();
    const { t } = useTranslation('pricing');
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const user = useAppSelector(selectUser);
    const [selectPricingPlan, { isLoading: isSelecting, isError: isSelectError, isSuccess }] = useSelectPricingPlanMutation();

    const navigate = useNavigate();

    useEffect(() => {
        if (isSuccess) {
            alert(t('selectSuccess'));
        }
        if (isSelectError) {
            alert(t('selectError'));
        }
        if (activeSubscription) {
            navigate('/pricing/update/');
        }
    }, [isSuccess, activeSubscription, isSelectError]);

    const handleSelectPlan = (planId: string) => {
        if (!user) {
            navigate('/sign-in/');
            return;
        }
        selectPricingPlan({ planId });
    };

    if (isError || !pricingPlans) {
        return <div>{t('errorLoading')}</div>;
    }

    if (activeSubscription) {
        return <div>{t('alreadySubscribed')}</div>;
    }

    return (
        <Container>
            <Heading>{t('title')}</Heading>
            <PricingPlanContainer>
                {pricingPlans.map((plan: PricingPlan) => (
                    <PricingPlanCard key={plan.id} plan={plan} onSelect={() => handleSelectPlan(String(plan.id))} isDisabled={isSelecting} />
                ))}
            </PricingPlanContainer>
        </Container>
    );
};


export const UpdatePricingPlanPage = () => {
    
    const { data: pricingPlans, isLoading, isError } = useGetPricingPlansQuery();
    const [changePricingPlan, { isLoading: isChanging, isError: isChangeError, isSuccess }] = useChangePricingPlanMutation();
    const activeSubscription = useAppSelector(selectActiveSubscription);
    const user = useAppSelector(selectUser);
    const { t } = useTranslation('pricing');
    const navigate = useNavigate();

    useEffect(() => {
        if (isSuccess) {
            alert(t('changeSuccess'));
            navigate('/backoffice/');
        }
    }, [isSuccess]);

    const handleChangePlan = (planId: string) => {
        changePricingPlan({ planId });
        if (!user) {
            navigate('/sign-in/');
        }
    };

    if (isLoading) {
        return <div>{t('loading')}</div>;
    }

    if (isError || !pricingPlans) {
        return <div>{t('errorLoading')}</div>;
    }
    if (!activeSubscription) {
        return <div>{t('notSubscribed')}</div>;
    }

    return (
        <Container>
            <Heading>{t('updateTitle')}</Heading>
            <PricingPlanContainer>
                {pricingPlans.filter((plan: PricingPlan) => plan.id !== activeSubscription?.pricing_plan?.id).map((plan: PricingPlan) => (
                    <PricingPlanCard key={plan.id} plan={plan} onSelect={handleChangePlan} />
                ))}
            </PricingPlanContainer>
            {isChanging && <div>{t('changingPlan')}</div>}
            {isChangeError && <div>{t('errorChanging')}</div>}
        </Container>
    )
};


export const ManagePricingPlanPage = () => {
    
    const activeSubscription: PricingPlanSubscription | null = useAppSelector(selectActiveSubscription) || null;
    const currentPricingPlan: PricingPlan | null = activeSubscription?.pricing_plan || null;
    const { t } = useTranslation('pricing');
    const navigate = useNavigate();
    const { data: upcomingSubscription, isLoading: isLoadingUpcoming, isError: isErrorUpcoming } = useGetUpcomingSubscriptionQuery();
    const [cancelSubscription, { isLoading: isCancelling, isError: isCancelError, isSuccess: isCancelSuccess }] = useCancelSubscriptionMutation();
    const { data: pricingPlans, isLoading: isLoadingPlans, isError: isLoadingPlansError } = useGetPricingPlansQuery();


    useEffect(() => {
        if (isCancelSuccess) {
            toast.success(t('cancelSuccess') as string);
            navigate('/');
        }
    }, [isCancelSuccess]);

    const handleCancelSubscription = () => {
        cancelSubscription({ immediately: false });
    };

    if (!activeSubscription) {
        return <div>{t('notSubscribed')}</div>;
    }

    return (
        <Container>
            <Heading>{t('manageTitle')}</Heading>
            <div>
                <h2>{t('currentPlan')}</h2>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('planName')}</td>
                            <td style={{ padding: '8px' }}>{currentPricingPlan?.name}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('price')}</td>
                            <td style={{ padding: '8px' }}>${currentPricingPlan?.price}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('billingCycle')}</td>
                            <td style={{ padding: '8px' }}>{currentPricingPlan?.billing_cycle}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('status')}</td>
                            <td style={{ padding: '8px' }}>{activeSubscription.status}</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #ddd' }}>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('startDate')}</td>
                            <td style={{ padding: '8px' }}>{new Date(activeSubscription.start_date).toLocaleDateString()}</td>
                        </tr>
                        <tr>
                            <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('endDate')}</td>
                            <td style={{ padding: '8px' }}>{new Date(activeSubscription.end_date).toLocaleDateString()}</td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div>
                <h2>{t('upcomingPlan')}</h2>
                {isLoadingUpcoming ? (
                    <div>{t('loading')}</div>
                ) : isErrorUpcoming || !upcomingSubscription?.upcoming_subscription ? (
                    <div>{t('noUpcoming')}</div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <tbody>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('planName')}</td>
                                <td style={{ padding: '8px' }}>{upcomingSubscription.pricing_plan?.name}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('price')}</td>
                                <td style={{ padding: '8px' }}>${upcomingSubscription.pricing_plan?.price}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('billingCycle')}</td>
                                <td style={{ padding: '8px' }}>{upcomingSubscription.pricing_plan?.billing_cycle}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('status')}</td>
                                <td style={{ padding: '8px' }}>{upcomingSubscription.upcoming_subscription.status}</td>
                            </tr>
                            <tr style={{ borderBottom: '1px solid #ddd' }}>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('startDate')}</td>
                                <td style={{ padding: '8px' }}>{new Date(upcomingSubscription.upcoming_subscription.start_date).toLocaleDateString()}</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '8px', fontWeight: 'bold' }}>{t('endDate')}</td>
                                { upcomingSubscription.upcoming_subscription.end_date === null ? (
                                    <td style={{ padding: '8px' }}>{t('indefinite')}</td>
                                ) : (
                                    <td style={{ padding: '8px' }}>{new Date(upcomingSubscription.upcoming_subscription.end_date).toLocaleDateString()}</td>
                                ) }
                            </tr>
                        </tbody>
                    </table>
                )}
            </div>
            <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
                <div style={{ flex: 1 }}>
                    <Button onClick={() => navigate('/pricing/update/')} style={{ width: '100%' }}>
                        {t('change')}
                    </Button>
                </div>
                <div style={{ flex: 1 }}>
                    <Button onClick={handleCancelSubscription} disabled={isCancelling} style={{ width: '100%', backgroundColor: '#dc2626' }}>
                        {isCancelling ? t('cancelling') : t('cancel')}
                    </Button>
                    {isCancelError && <div>{t('errorCancelling')}</div>}
                </div>
            </div>
        </Container>
    )
}
